// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import config from "@config";
import { sortToString } from "@transformers/sort";
import actionCreators from "@modules/service/actionCreator";
import type {
  AbsenceReasonsResponse,
  AbsenceReason,
} from "@modules/service/reducer/absence-reasons/types";
import { ApprovalStateEnum } from "@modules/service/constants";
import type { HierarchyAssignment, ApprovableContainer } from "@modules/service/types";
import {
  GetAbsenceReasonActive,
  AddNewAbsenceReason,
  GetAbsenceReason,
  UpdateAbsenceReason,
  SubmitAbsenceReasonForReview,
  CancelAbsenceReasonReview,
  StartAbsenceReasonDraft,
  DiscardAbsenceReasonDraft,
  CancelAbsenceReasonReviewAndDiscard,
  DuplicateAbsenceReason,
  DisableAbsenceReason,
  GetAbsenceReasonsList,
  GetAbsenceReasonsActiveList,
  GetAbsenceReasonsPendingList,
  GetAbsenceReasonsDraftList,
  ExportLiveAbsenceReasons,
} from "@modules/service/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import {
  selectAbsenceReasonsPage,
  selectActiveAbsenceReasonsPage,
  selectPendingAbsenceReasonsPage,
  selectDraftAbsenceReasonsPage,
  getAbsenceReasonsSort,
  getActiveAbsenceReasonsSort,
  getPendingAbsenceReasonsSort,
  getDraftAbsenceReasonsSort,
} from "@modules/service/selectors/absence-reasons";
import { isFirstCareUser } from "@modules/account/selectors";
import { downloadCsvFile } from "@sagas/utils";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ItemResponse<T> = $Exact<T>;

type AbsenceReasonsListRequest = $Exact<{
  clientId: string,
  page: number | null,
}>;

type AddAbsenceReasonsRequest = $Exact<{
  clientId: string,
  draft: AbsenceReason,
  assignments: Array<HierarchyAssignment>,
}>;

export type AbsenceReasonRequest = $Exact<{
  ...AbsenceReasonsListRequest,
  stableId: string,
}>;

export type AbsenceReasonSubmitForReviewRequest = $Exact<{
  ...AbsenceReasonRequest,
  notes: string,
}>;
type AbsenceReasonUpdateRequest = $Exact<{
  ...AbsenceReasonRequest,
  draft: AbsenceReason,
}>;

export function* onGetAbsenceReasonsList({
  payload,
}: Action<AbsenceReasonsListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<AbsenceReason>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }
    const currentPage = yield select(selectAbsenceReasonsPage);
    const page = (payload && payload.page) || currentPage;
    const sort = yield select(getAbsenceReasonsSort);

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason`,
      {
        params: {
          page,
          limit: config.results1000PerPage,
          sort: sortToString(sort),
        },
      },
    );

    yield put(actionCreators.GetAbsenceReasonsListSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAbsenceReasonsListError(e.response));
  }
}

export function* onGetAbsenceReasonsActiveList({
  payload,
}: Action<AbsenceReasonsListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<AbsenceReasonsResponse>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }
    const currentPage = yield select(selectActiveAbsenceReasonsPage);
    const page = (payload && payload.page) || currentPage;
    const sort = yield select(getActiveAbsenceReasonsSort);

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/active`,
      {
        params: {
          page,
          limit: config.results100PerPage,
          sort: sortToString(sort),
        },
      },
    );

    yield put(actionCreators.GetAbsenceReasonsActiveListSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAbsenceReasonsActiveListError(e.response));
  }
}

export function* onGetAbsenceReasonsPendingList({
  payload,
}: Action<AbsenceReasonsListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<AbsenceReasonsResponse>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }
    const currentPage = yield select(selectPendingAbsenceReasonsPage);
    const page = (payload && payload.page) || currentPage;
    const sort = yield select(getPendingAbsenceReasonsSort);

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/pending-review`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
          sort: sortToString(sort),
        },
      },
    );

    yield put(actionCreators.GetAbsenceReasonsPendingListSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAbsenceReasonsPendingListError(e.response));
  }
}

export function* onGetAbsenceReasonsDraftList({
  payload,
}: Action<AbsenceReasonsListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<AbsenceReasonsResponse>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }
    const currentPage = yield select(selectDraftAbsenceReasonsPage);
    const page = (payload && payload.page) || currentPage;
    const sort = yield select(getDraftAbsenceReasonsSort);

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/draft`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
          sort: sortToString(sort),
        },
      },
    );

    yield put(actionCreators.GetAbsenceReasonsDraftListSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAbsenceReasonsDraftListError(e.response));
  }
}

export function* onGetAbsenceReasonActive({
  payload,
}: Action<AbsenceReasonRequest>): Generator<*, void, { data: AbsenceReason }> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/${payload.stableId}/active`,
    );

    yield put(actionCreators.GetAbsenceReasonActiveSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAbsenceReasonActiveError(e.response));
  }
}

export function* onAddNewAbsenceReason({
  payload,
}: Action<AddAbsenceReasonsRequest>): Generator<*, void, { data: AbsenceReason }> {
  try {
    if (!payload || !payload.clientId || !payload.draft) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason`,
      payload.draft,
    );

    const firstCareUser = yield select(isFirstCareUser);

    if (firstCareUser) {
      yield call(
        http.post,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/${response.data.id}/submit-for-review`,
        {
          notes: "Absence Reason added and submitted for review",
        },
      );
    }

    yield all([
      put(actionCreators.GetAbsenceReasonActiveSuccess(response.data)),
      put(actionCreators.GetAbsenceReasonsDraftList({ clientId: payload.clientId })),
      put(actionCreators.GetAbsenceReasonsActiveList({ clientId: payload.clientId })),
      put(actionCreators.ShowAddAbsenceReasonModal(false)),
      put(sendSuccessMessage("ABSENCE_REASON_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddNewAbsenceReasonError(e.response));
  }
}

export function* onGetAbsenceReason({
  payload,
}: Action<AbsenceReasonRequest>): Generator<*, void, { data: AbsenceReason }> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/${payload.stableId}`,
    );

    yield put(actionCreators.GetAbsenceReasonSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAbsenceReasonError(e.response));
  }
}

export function* onUpdateAbsenceReason({
  payload,
}: Action<AbsenceReasonUpdateRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<AbsenceReason>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.stableId || !payload.draft) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/${payload.stableId}/draft`,
      payload.draft,
    );

    const firstCareUser = yield select(isFirstCareUser);

    if (firstCareUser) {
      yield call(
        http.post,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/${payload.stableId}/submit-for-review`,
        {
          notes: "Absence Reason updated and submitted for review",
        },
      );
    }

    yield all([
      put(actionCreators.UpdateAbsenceReasonSuccess(response.data)),
      put(actionCreators.ShowAbsenceReasonEditModal(false)),
      put(
        actionCreators.GetAbsenceReasonsDraftList({
          clientId: payload.clientId,
        }),
      ),
      put(
        actionCreators.GetAbsenceReasonsActiveList({
          clientId: payload.clientId,
        }),
      ),
      put(push(route("ADMINISTRATION_SERVICE_ABSENCE_REASONS", { clientId: payload.clientId }))),
      put(sendSuccessMessage("DRAFT_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateAbsenceReasonError(e.response));
  }
}

export function* onSubmitAbsenceReasonForReview({
  payload,
}: Action<AbsenceReasonSubmitForReviewRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId || !payload.notes) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/${payload.stableId}/submit-for-review`,
      {
        notes: payload.notes,
      },
    );

    yield all([
      put(actionCreators.SubmitAbsenceReasonForReviewSuccess()),
      put(actionCreators.SetShowSubmitAbsenceReasonModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_ABSENCE_REASONS", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.SubmitAbsenceReasonForReviewError(e.response));
  }
}

export function* onCancelAbsenceReasonReview({
  payload,
}: Action<AbsenceReasonRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/${payload.stableId}/cancel-review`,
    );

    yield all([
      put(actionCreators.CancelAbsenceReasonReviewSuccess()),
      put(actionCreators.SetShowCancelAbsenceReasonModal(false)),
      put(
        actionCreators.GetAbsenceReasonsDraftList({
          clientId: payload.clientId,
        }),
      ),
      put(
        actionCreators.GetAbsenceReasonsPendingList({
          clientId: payload.clientId,
        }),
      ),
      put(push(route("ADMINISTRATION_SERVICE_ABSENCE_REASONS", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelAbsenceReasonReviewError(e.response));
  }
}

export function* onStartAbsenceReasonDraft({
  payload,
}: Action<AbsenceReasonRequest>): Generator<*, void, { data: AbsenceReason }> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/${payload.stableId}`,
    );

    yield all([
      put(actionCreators.StartAbsenceReasonDraftSuccess(response.data)),
      put(actionCreators.ShowAbsenceReasonEditModal(true)),
    ]);
  } catch (e) {
    yield put(actionCreators.StartAbsenceReasonDraftError(e.response));
  }
}

export function* onDiscardAbsenceReasonDraft({
  payload,
}: Action<AbsenceReasonRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/${payload.stableId}/draft`,
    );

    yield all([
      put(actionCreators.DiscardAbsenceReasonDraftSuccess()),
      put(actionCreators.SetShowDiscardAbsenceReasonModal(false)),
      put(
        actionCreators.GetAbsenceReasonsDraftList({
          clientId: payload.clientId,
        }),
      ),
      put(push(route("ADMINISTRATION_SERVICE_ABSENCE_REASONS", { clientId: payload.clientId }))),
    ]);
    yield call(onRefresh, { clientId: payload.clientId });
  } catch (e) {
    yield put(actionCreators.DiscardAbsenceReasonDraftError(e.response));
  }
}

export function* onCancelAbsenceReasonReviewAndDiscard({
  payload,
}: Action<AbsenceReasonRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/${payload.stableId}/cancel-review`,
    );
    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/${payload.stableId}/draft`,
    );

    yield all([
      put(actionCreators.CancelAbsenceReasonReviewSuccess()),
      put(actionCreators.SetShowCancelDiscardAbsenceReasonModal(false)),
      put(
        actionCreators.GetAbsenceReasonsDraftList({
          clientId: payload.clientId,
        }),
      ),
      put(
        actionCreators.GetAbsenceReasonsPendingList({
          clientId: payload.clientId,
        }),
      ),
      put(push(route("ADMINISTRATION_SERVICE_ABSENCE_REASONS", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelAbsenceReasonReviewAndDiscardError(e.response));
  }
}

export function* onDuplicateAbsenceReason({
  payload,
}: Action<AbsenceReasonRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<AbsenceReason>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/${payload.stableId}/active`,
    );

    yield all([
      put(actionCreators.DuplicateAbsenceReasonSuccess(response.data)),
      put(actionCreators.ShowAddAbsenceReasonModal(true)),
      put(push(route("ADMINISTRATION_SERVICE_ABSENCE_REASONS", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.DuplicateAbsenceReasonError(e.response));
  }
}

export type AbsenceReasonDisableRequest = $Exact<{
  ...AbsenceReasonRequest,
  approvable: ApprovableContainer<AbsenceReason>,
}>;

export function* onDisableAbsenceReason({
  payload,
}: Action<AbsenceReasonDisableRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error("Invalid payload provided");
    }
    const draft =
      payload.approvable.approvalState === ApprovalStateEnum.ACTIVE_ONLY
        ? payload.approvable.active
        : payload.approvable.draft;

    if (!draft) {
      throw new Error("Invalid draft");
    }

    const response = yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/${payload.stableId}/draft`,
      {
        ...draft,
        isActive: !draft.isActive,
      },
    );

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/${payload.stableId}/submit-for-review`,
      {
        notes: "Absence Reason disabled and resubmitted for review",
      },
    );

    yield all([
      put(actionCreators.DisableAbsenceReasonSuccess(response.data)),
      put(actionCreators.SetShowDisableAbsenceReasonModal(false)),
      put(
        actionCreators.GetAbsenceReasonsDraftList({
          clientId: payload.clientId,
        }),
      ),
      put(push(route("ADMINISTRATION_SERVICE_ABSENCE_REASONS", { clientId: payload.clientId }))),
      put(sendSuccessMessage("DRAFT_UPDATED")),
    ]);
    yield call(onRefresh, { clientId: payload.clientId });
  } catch (e) {
    yield put(actionCreators.DisableAbsenceReasonError(e.response));
  }
}

// REFRESH

type RefreshRequest = $Exact<{
  clientId: string,
  stableId?: string,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  try {
    // yield put(actionCreators.GetAbsenceReasonsList({ clientId: params.clientId }));
    if (params.stableId) {
      yield put(
        actionCreators.GetAbsenceReason({ clientId: params.clientId, stableId: params.stableId }),
      );
    }
  } catch (e) {
    throw e;
  }
}

function* onExportLiveAbsenceReasons({
  payload,
}: Action<{ clientId: number }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }
    const response = yield call(
      http.get,
      `api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/active/export`,
    );
    downloadCsvFile(response.data, `Absence_Reasons-${payload.clientId}.csv`);
    yield put(actionCreators.ExportLiveAbsenceReasonsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportLiveAbsenceReasonsError(e.response));
  }
}

export function* AbsenceReasonsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetAbsenceReasonsList, onGetAbsenceReasonsList),
    takeEvery(GetAbsenceReasonsActiveList, onGetAbsenceReasonsActiveList),
    takeEvery(GetAbsenceReasonsPendingList, onGetAbsenceReasonsPendingList),
    takeEvery(GetAbsenceReasonsDraftList, onGetAbsenceReasonsDraftList),
    takeEvery(GetAbsenceReasonActive, onGetAbsenceReasonActive),
    takeEvery(AddNewAbsenceReason, onAddNewAbsenceReason),
    takeEvery(GetAbsenceReason, onGetAbsenceReason),
    takeEvery(UpdateAbsenceReason, onUpdateAbsenceReason),
    takeEvery(SubmitAbsenceReasonForReview, onSubmitAbsenceReasonForReview),
    takeEvery(CancelAbsenceReasonReview, onCancelAbsenceReasonReview),
    takeEvery(StartAbsenceReasonDraft, onStartAbsenceReasonDraft),
    takeEvery(DiscardAbsenceReasonDraft, onDiscardAbsenceReasonDraft),
    takeEvery(CancelAbsenceReasonReviewAndDiscard, onCancelAbsenceReasonReviewAndDiscard),
    takeEvery(DuplicateAbsenceReason, onDuplicateAbsenceReason),
    takeEvery(DisableAbsenceReason, onDisableAbsenceReason),
    takeEvery(ExportLiveAbsenceReasons, onExportLiveAbsenceReasons),
  ]);
}

export default AbsenceReasonsSagas;
